import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { db } from '../../lib/firebase';

/**
 * `participants/{uid}/memberships/*` を読み出して `MembershipSummary[]` を返す hook
 * (Sprint BG-DataSettings 9、GAP U-S1)。
 *
 * 旧 `old/apps/user/src/pages/DistrictHomePage.tsx` L73-113 の inline `getDocs` ロジックを
 * v2 で独立 hook 化:
 *   - `orderBy('lastSeenAt', 'desc')` で最近アクセスした地区を上に表示
 *   - `Timestamp` → `number` (millis) に正規化 (UI 側 sort / format 容易化)
 *   - 旧 `joinedSessionIds` (array) の length を `joinedSessionCount` として exposure
 *   - 旧 v1 にあった `setReloadTick` 経由の再取得を `refresh()` callback で提供
 *
 * subscribe は行わない (一覧 ホーム画面表示時の snapshot で十分、参加直後は call 元から refresh)。
 *
 * 失敗時は `error` を返す (旧 v1 の console.error + error banner と同等)。
 *
 * **Phase 5 minimum**: docs/04 §4 の memberships 必須フィールドのうち
 * `districtName / districtToken / nickname / joinedSessionIds / lastSeenAt` のみ参照。
 */

export interface MembershipSummary {
  districtId: string;
  districtName: string;
  /** 地区 URL トークン (route `/m/:urlToken` 遷移用)。空文字なら遷移 disable。 */
  districtToken: string;
  nickname: string;
  joinedSessionCount: number;
  /** ms epoch、未設定なら 0。 */
  lastSeenAt: number;
}

export type UseMyMembershipsState =
  | { status: 'idle'; memberships: MembershipSummary[]; error: null }
  | { status: 'loading'; memberships: MembershipSummary[]; error: null }
  | { status: 'ready'; memberships: MembershipSummary[]; error: null }
  | { status: 'error'; memberships: MembershipSummary[]; error: Error };

export interface UseMyMembershipsReturn {
  state: UseMyMembershipsState;
  refresh: () => void;
}

export function useMyMemberships(uid: string | null): UseMyMembershipsReturn {
  const [state, setState] = useState<UseMyMembershipsState>({
    status: 'idle',
    memberships: [],
    error: null,
  });
  const [reloadTick, setReloadTick] = useState(0);

  const refresh = useCallback(() => {
    setReloadTick((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!uid) {
      setState({ status: 'idle', memberships: [], error: null });
      return;
    }
    let cancelled = false;
    setState((prev) => ({ status: 'loading', memberships: prev.memberships, error: null }));
    (async () => {
      try {
        const q = query(
          collection(db, 'participants', uid, 'memberships'),
          orderBy('lastSeenAt', 'desc'),
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        const list: MembershipSummary[] = snap.docs.map((d) => {
          const data = d.data() as Record<string, unknown>;
          const ts = data.lastSeenAt as { toMillis?: () => number } | undefined;
          const lastSeen = ts && typeof ts.toMillis === 'function' ? ts.toMillis() : 0;
          const joinedSessionIds = Array.isArray(data.joinedSessionIds)
            ? (data.joinedSessionIds as unknown[])
            : [];
          return {
            districtId: d.id,
            districtName: typeof data.districtName === 'string' ? data.districtName : d.id,
            districtToken: typeof data.districtToken === 'string' ? data.districtToken : '',
            nickname: typeof data.nickname === 'string' ? data.nickname : '',
            joinedSessionCount: joinedSessionIds.length,
            lastSeenAt: lastSeen,
          };
        });
        setState({ status: 'ready', memberships: list, error: null });
      } catch (e) {
        if (cancelled) return;
        setState({
          status: 'error',
          memberships: [],
          error: e as Error,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, reloadTick]);

  return { state, refresh };
}
